import { useEffect, useMemo, useState, useRef } from "react";
import { Dialog } from "@headlessui/react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { PMField } from "../../../utils/PMField";
import { useDispatch, useSelector } from "react-redux";
import { dispatchWithToast, mapErrors } from "../../../Utils";
import { ProjectSelector } from "../../../utils/moduleSelectors/projectSelector";
import { CustomerSelector } from "../../../utils/moduleSelectors/customerSelector";
import {
  createDeviationAsync,
  updateDeviationAsync,
} from "../../../../slices/quality/deviation";
import {
  getDeviationTypeOptions,
  priorityOptions,
  severityOptions,
} from "./utils";
import { fetchDeviation } from "../../../../api/quality/deviation";
import { fetchUsers } from "../../../../api/user";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ProjectFolderSelector } from "../../../utils/moduleSelectors/projectFolderSelector";
import { PMDatePicker } from "../../../utils/PMDatePicker";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { WorkOrderSelector } from "../../../utils/moduleSelectors/workOrderSelector";

export default function DeviationNewDialog(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    deviation: {},
  });

  const { open, onClose, selected, project, customer } = props;

  const { isLoading, deviation } = itemState;

  const [deviationTypeOptions, setDeviationTypeOptions] = useState([]);
  const formikRef = useRef(null);
  const [showSeverityDialog, setShowSeverityDialog] = useState(false);
  const [showPriorityDialog, setShowPriorityDialog] = useState(false);
  const [showDeviationTypeDialog, setShowDeviationTypeDialog] = useState(false);
  const [showResponsibleUserSelector, setShowResponsibleUserSelector] = useState(false);
  const titleInputRef = useRef(null);
  const [employeeState, setEmployeeState] = useState({
    isLoading: false,
    data: [],
  });
  const [employeeSearch, setEmployeeSearch] = useState("");


  const getDeviation = async () => {
    await fetchDeviation(selected.id)
      .then((res) => {
        if (res.data.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            deviation: res.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          deviation: {},
          error: true,
          errors: errors,
        }));
      });
  };

  useEffect(() => {
    async function fetchData() {
      const fetchedOptions = await getDeviationTypeOptions();
      setDeviationTypeOptions(fetchedOptions);
    }
    async function fetchEmployees() {
      setEmployeeState((prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await fetchUsers({
          filters: [{ name: "role", value: "deviations" }],
          page: 1,
          per_page: 50,
          query: "",
          sort: "asc",
          sortBy: "name",
          view: "all",
          fields: "id,name,displayTitle,email",
        });
        setEmployeeState({
          isLoading: false,
          data: Array.isArray(response?.data) ? response.data : [],
        });
      } catch (_error) {
        setEmployeeState({ isLoading: false, data: [] });
      }
    }

    if (open) {
      setItemState((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
      if (selected?.id) {
        getDeviation(selected?.id);
      } else {
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
        }));
      }
      setTimeout(() => {
        fetchData();
      });
      fetchEmployees();
    } else {
      setItemState((prevState) => ({
        ...prevState,
        deviation: {},
      }));
      setShowSeverityDialog(false);
      setShowPriorityDialog(false);
      setShowDeviationTypeDialog(false);
      setShowResponsibleUserSelector(false);
      formikRef.current = null;
      setEmployeeSearch("");
    }
  }, [open]);

  const filteredEmployees = useMemo(() => {
    const query = employeeSearch.trim().toLowerCase();
    if (!query) return employeeState.data;
    return employeeState.data.filter((employee) => {
      const haystack = [
        employee?.displayTitle,
        employee?.name,
        employee?.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [employeeSearch, employeeState.data]);

  // Focus title input when dialog opens
  useEffect(() => {
    if (open && !isLoading && titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);
    }
  }, [open, isLoading]);

  const createOrUpdateDispatch = (data) => {
    if (deviation?.id) {
      const res = dispatch(updateDeviationAsync(data));
      return res;
    } else {
      const res = dispatch(createDeviationAsync(data));
      return res;
    }
  };

  const DeviationForm = () => (
    <Formik
      initialValues={{
        id: deviation?.id || null,
        title: deviation?.title || "",
        severity: deviation?.severity || "1",
        priority: deviation?.priority || "1",
        reference: deviation?.reference || "",
        customer_id: deviation?.customer?.id || customer?.id,
        project_id: deviation?.project?.id || project?.id,
        deviation_type_id: deviation?.deviation_type?.id || null,
        deviation_date:
          deviation?.date || new Date().toISOString().split('T')[0],
        description: deviation?.description || "",
        proposed_measures: deviation?.proposed_measures || "",
        number: deviation?.number || "",
        responsible_user_id: deviation?.responsible_user?.id || currentUser?.id,
        assigned_user_id: deviation?.assigned_user?.id || null,
        work_order_id: deviation?.work_order?.id || null,
        folder_id: deviation?.folder?.id || null,
        submit: null,
      }}
      validationSchema={Yup.object({
        // customer_id: Yup.string().required(
        //   t("customer.required")
        // ),
        // customer_id: Yup.string().required("Customer is required"),
        // project_id: Yup.string().required("Customer is required"),
        deviation_type_id: Yup.string().required("Deviation type is required"),
      })}
      onSubmit={(values, helpers) => {
        try {
          dispatchWithToast(
            createOrUpdateDispatch(values),
            props.onRefresh,
            onClose(true)
          );

          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        } catch (err) {
          console.log(err);
          toast.error("Something went wrong...");
          console.error(err);
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => {
        formikRef.current = formik;
        const selectedResponsibleUser = employeeState.data.find(
          (employee) => String(employee.id) === String(formik.values.responsible_user_id)
        );
        
        return (
        <Form>
          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-6">
              <PMField
                name="title"
                type="text"
                label={t("Title")}
                ref={titleInputRef}
                autoFocus
                {...formik}
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <PMDatePicker
                name="deviation_date"
                label={t("Deviation Date")}
                value={formik.values.deviation_date}
                onChange={(e) => {
                  formik.setFieldValue("deviation_date", e.target.value);
                }}
                errors={formik.errors.deviation_date}
                touched={formik.touched.deviation_date}
              />
            </div>

            {!props?.customer?.id && (
              <div className="col-span-6 sm:col-span-3">
                <CustomerSelector
                  selected={
                    deviation?.customer?.id ? deviation?.customer : customer
                  }
                  {...formik}
                />
              </div>
            )}

            {!props?.project?.id && (
              <div className="col-span-6 sm:col-span-3">
                <ProjectSelector
                  selected={
                    deviation?.project?.id ? deviation?.project : project
                  }
                  {...formik}
                />
              </div>
            )}

            {isAdmin && (
              <div className="col-span-6 sm:col-span-3">
                <ProjectFolderSelector
                  projectId={formik.values?.project_id || project?.id}
                  selected={deviation?.folder?.id ? deviation?.folder : null}
                  {...formik}
                />
              </div>
            )}

            <div className="col-span-6 sm:col-span-3">
              <WorkOrderSelector
                selected={deviation?.work_order || null}
                project={project || deviation?.project}
                {...formik}
              />
            </div>

            <div className="col-span-6">
              <PMField
                name="description"
                label={t("Description")}
                multiline={true}
                className="min-h-[120px] resize-y"
                placeholder={t("Describe the deviation")}
                {...formik}
              />
            </div>

            {/* <div className="col-span-6 sm:col-span-3">
              <PMField
                name="reference"
                type="text"
                label={t("Reference")}
                {...formik}
              />
            </div> */}

            <div className="col-span-6 sm:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("Deviation Type")}
              </label>
              {(() => {
                const selectedType = deviationTypeOptions.find((opt) => opt.value === formik.values.deviation_type_id);
                return (
                  <button
                    type="button"
                    onClick={() => setShowDeviationTypeDialog(true)}
                    className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                      selectedType
                        ? "bg-blue-50 text-blue-700 border-blue-300"
                        : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    {selectedType?.title || t("Select Deviation Type")}
                  </button>
                );
              })()}
              {formik.errors.deviation_type_id && formik.touched.deviation_type_id && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.deviation_type_id}</p>
              )}
            </div>

            <div className="col-span-6 sm:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("Responsible User")}
              </label>
              {(() => {
                return (
                  <button
                    type="button"
                    onClick={() => setShowResponsibleUserSelector(true)}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm text-slate-700 transition hover:border-slate-300 hover:bg-slate-100"
                  >
                    <div className="font-medium text-slate-900">
                      {selectedResponsibleUser?.displayTitle || selectedResponsibleUser?.name || t("Select Responsible User")}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {selectedResponsibleUser?.email || t("Responsible for follow-up")}
                    </div>
                  </button>
                );
              })()}
            </div>

            <div className="col-span-6 sm:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("Severity")}
              </label>
              {(() => {
                const selectedSeverity = severityOptions.find((opt) => opt.value === formik.values.severity);
                return (
                  <button
                    type="button"
                    onClick={() => setShowSeverityDialog(true)}
                    className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                      selectedSeverity
                        ? `${selectedSeverity.class} border-gray-300`
                        : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    {selectedSeverity?.label || t("Select Severity")}
                  </button>
                );
              })()}
              {formik.errors.severity && formik.touched.severity && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.severity}</p>
              )}
            </div>

            <div className="col-span-6 sm:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("Priority")}
              </label>
              {(() => {
                const selectedPriority = priorityOptions.find((opt) => opt.value === formik.values.priority);
                return (
                  <button
                    type="button"
                    onClick={() => setShowPriorityDialog(true)}
                    className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                      selectedPriority
                        ? `${selectedPriority.class} border-gray-300`
                        : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    {selectedPriority?.label || t("Select Priority")}
                  </button>
                );
              })()}
              {formik.errors.priority && formik.touched.priority && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.priority}</p>
              )}
            </div>
          </div>

          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            <button
              type="button"
              className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
              onClick={onClose}
            >
              {t("Close")}{" "}
            </button>

            <button
              type="button"
              className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
              onClick={formik.submitForm}
            >
              {formik?.values?.id ? t("Update") : t("Create")}
            </button>
          </div>
        </Form>
        );
      }}
    </Formik>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} className="relative z-10">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" />

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
            <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <div className="mt-3 sm:mt-5">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  <div className="flex items-center py-5 w-full">
                    <div className="block">
                      <h2
                        id="applicant-information-title"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        {t("Create Deviation")}
                      </h2>
                    </div>
                  </div>
                </Dialog.Title>
                {isLoading ? <ResourceLoading /> : <DeviationForm />}
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>

      {/* Severity Selection Dialog */}
      {showSeverityDialog && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
            onClick={() => setShowSeverityDialog(false)}
            aria-hidden="true"
          />
          <div 
            className="relative bg-white rounded-lg shadow-xl w-full max-w-xs p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                {t("Select Severity")}
              </h3>
              <button
                onClick={() => setShowSeverityDialog(false)}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-1">
              {severityOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      if (formikRef.current) {
                        formikRef.current.setFieldValue("severity", opt.value);
                        formikRef.current.setTouched({ ...formikRef.current.touched, severity: true });
                      }
                      setShowSeverityDialog(false);
                    }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    String(formikRef.current?.values?.severity) === String(opt.value)
                      ? `${opt.class} font-medium`
                      : "hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Priority Selection Dialog */}
      {showPriorityDialog && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
            onClick={() => setShowPriorityDialog(false)}
            aria-hidden="true"
          />
          <div 
            className="relative bg-white rounded-lg shadow-xl w-full max-w-xs p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                {t("Select Priority")}
              </h3>
              <button
                onClick={() => setShowPriorityDialog(false)}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-1">
              {priorityOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      if (formikRef.current) {
                        formikRef.current.setFieldValue("priority", opt.value);
                        formikRef.current.setTouched({ ...formikRef.current.touched, priority: true });
                      }
                      setShowPriorityDialog(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      formikRef.current?.values?.priority === String(opt.value)
                        ? `${opt.class} font-medium`
                        : "hover:bg-gray-100 text-gray-700"
                    }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Deviation Type Selection Dialog */}
      {showDeviationTypeDialog && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
            onClick={() => setShowDeviationTypeDialog(false)}
            aria-hidden="true"
          />
          <div 
            className="relative bg-white rounded-lg shadow-xl w-full max-w-xs p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                {t("Select Deviation Type")}
              </h3>
              <button
                onClick={() => setShowDeviationTypeDialog(false)}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {deviationTypeOptions.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Laster typer...</p>
              ) : (
                deviationTypeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      if (formikRef.current) {
                        formikRef.current.setFieldValue("deviation_type_id", opt.value);
                        formikRef.current.setTouched({ ...formikRef.current.touched, deviation_type_id: true });
                      }
                      setShowDeviationTypeDialog(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      String(formikRef.current?.values?.deviation_type_id) === String(opt.value)
                        ? "bg-blue-100 text-blue-700 font-medium"
                        : "hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    {opt.title}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showResponsibleUserSelector && createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
            onClick={() => setShowResponsibleUserSelector(false)}
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-lg overflow-hidden rounded-[1.5rem] bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{t("Select Responsible User")}</h3>
                  <p className="mt-1 text-sm text-slate-500">{t("Choose who should own this deviation")}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowResponsibleUserSelector(false)}
                  className="rounded-md p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="relative mt-4">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  placeholder={t("Search employees")}
                  className="h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="max-h-[26rem] overflow-y-auto px-3 py-3">
              {employeeState.isLoading ? (
                <div className="px-3 py-8 text-center text-sm text-slate-500">{t("Loading...")}</div>
              ) : filteredEmployees.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-slate-500">{t("No employees found")}</div>
              ) : (
                filteredEmployees.map((employee) => {
                  const isSelected =
                    String(employee.id) === String(formikRef.current?.values?.responsible_user_id);

                  return (
                    <button
                      key={employee.id}
                      type="button"
                      onClick={() => {
                        if (formikRef.current) {
                          formikRef.current.setFieldValue("responsible_user_id", employee.id);
                        }
                        setShowResponsibleUserSelector(false);
                      }}
                      className={`mb-2 flex w-full items-start justify-between rounded-2xl px-4 py-3 text-left transition ${
                        isSelected
                          ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                          : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <div>
                        <div className="font-medium">{employee.displayTitle || employee.name}</div>
                        <div className="mt-1 text-xs text-slate-500">{employee.email || t("No email")}</div>
                      </div>
                      {isSelected ? (
                        <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                          {t("Selected")}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
